import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  clearRegistrationData,
  setStepThreeData,
} from "../forms/registrationSlice";
import { RootState } from "../forms/store";
import { registerUser } from "../../api/loginAndReg";

import Modal from "../elements/Modal";
import { REGISTRATION_STEP_THREE } from "./Validations";
import { ReactComponent as SuccessIcon } from "../../resources/icons/success-reg.svg";
import { useAuthContext } from "../../contexts/authContext";
import { acceptSupervisionInvite } from "../../api/supervision";

const RegisterStepThree = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registrationData = useSelector(
    (state: RootState) => state.registration
  );
  const { setUser } = useAuthContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="w-[calc(100%*5/9)] flex flex-col justify-center">
      <div className="w-1/2 self-center">
        <h2 className="mb-8 text-3xl font-bold">Sign up to Build Connect</h2>
        <p className="mb-8 text-gray-500">
          We need some personal details to finalize your account.
        </p>

        <Formik
          initialValues={{
            name: registrationData.name,
            phone: registrationData.phone,
          }}
          validationSchema={REGISTRATION_STEP_THREE}
          onSubmit={async (values) => {
            dispatch(setStepThreeData(values));

            const registrationValues = {
              email: registrationData.email,
              password: registrationData.password,
              name: values.name,
              phone: values.phone,
              role: registrationData.role,
            };

            try {
              const response = await registerUser(registrationValues);
              const { token, user } = response;

              localStorage.setItem("token", token);
              localStorage.setItem("userType", user?.type || "UNKNOWN");

              setUser(user);
              dispatch(clearRegistrationData());

              const fcmToken = localStorage.getItem("fcmToken");
              if (fcmToken) {
                await fetch(
                  `${process.env.REACT_APP_URL}/notifications/subscribe`,
                  {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ token: fcmToken }),
                  }
                );
              }

              const inviteToken = localStorage.getItem("supervisorInviteToken");
              if (inviteToken) {
                const { data } = await acceptSupervisionInvite(inviteToken);

                if (data) {
                  const projectId = data?.project?.id || data?.projectId;
                  if (typeof projectId === "number") {
                    localStorage.removeItem("supervisorInviteToken");
                    navigate("/projects");
                    return;
                  } else {
                  }
                }
              }

              setIsModalOpen(true);
            } catch {}
          }}
        >
          {({ isValid, dirty }) => (
            <Form>
              <div className="relative mb-4">
                <label className="text-grey-700 block text-sm font-semibold mb-2">
                  Name*:
                </label>
                <Field
                  type="text"
                  name="name"
                  className="w-full p-3 border-2 rounded-lg"
                  placeholder="Enter your name"
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-red-500"
                />
              </div>

              <div className="relative mb-4">
                <label className="text-grey-700 block text-sm font-semibold mb-2">
                  Phone number*:
                </label>
                <Field
                  type="text"
                  name="phone"
                  className="w-full p-3 border-2 rounded-lg"
                  placeholder="Enter your phone"
                />
                <ErrorMessage
                  name="phone"
                  component="div"
                  className="text-red-500"
                />
              </div>

              <button
                className="blue-button w-full py-3 bg-blue-500 text-white font-semibold rounded-lg"
                type="submit"
                disabled={!(isValid && dirty)}
              >
                Sign up
              </button>

              <div className="flex justify-center mt-4">
                <small className="mr-2 block text-sm text-gray-500 mt-1">
                  Already have an account?
                </small>
                <button
                  type="button"
                  className="text-blue-600"
                  onClick={() => navigate("/login")}
                >
                  Log in
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <Modal isOpen={isModalOpen}>
        <div className="p-6 flex flex-col">
          <SuccessIcon className="mb-4" />
          <h2 className="font-bold text-xl mb-1">Welcome!</h2>
          <p className="mb-8 text-grey-800">
            Your account has been successfully created.
          </p>
          <button
            className="bg-[#1672EC] text-white rounded-md px-4 py-2 text-center"
            onClick={() => {
              navigate("/projects", { replace: true });
            }}
          >
            Got it!
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default RegisterStepThree;
